/* eslint-disable react/display-name */
import React, { memo, useCallback, useState } from  'react';
import styled from 'styled-components';
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, XAxis } from 'recharts';

import { translate } from '../../common/translation/translate';
import { largeMediaQuery, mediumMediaQuery, smallMediaQuery } from '../../common/utilities/sizeHelpers';
import { AlertsListHeader } from '../alerts/alertsListHeader';
import { OutdoorSportsForecastRecordType, OutdoorSportsIndexLegendLevel } from '../common/interfaces/types';
import { withScreenSize } from '../common/utilities/withScreenSize.jsx';
import { OutdoorSportsLoadingErrorUI } from './fallbackUI/outdoorSportsLoadingErrorUI';
import { HourlyChartLoadingUI } from './fallbackUI/hourlyChartLoadingUI';
import { InfoIcon }  from '../common/icons/infoIcon';
import { OutdoorSportsIndexExplanatoryPopup } from './outdoorSportsIndexExplanatoryPopup';

interface HourlyChartType {
    outdoorSportsDataIsFetching: boolean;
    forecastRecords: OutdoorSportsForecastRecordType[];
    isApiError: boolean;
    outdoorSportsIndexLegendLevels: OutdoorSportsIndexLegendLevel[];
    isDesktop: boolean;
    isMobile: boolean;
    windowWidth: number;
}

export const HourlyChart = withScreenSize(memo(({
    outdoorSportsDataIsFetching,
    forecastRecords,
    isApiError,
    outdoorSportsIndexLegendLevels,
    isMobile
}: HourlyChartType) => {
    const [ showIndexPopup, setShowIndexPopup ] = useState(false);
    const barWdith = isMobile ? 5 : 20;

    const handleInfoIconClick = useCallback(() => {
        setShowIndexPopup(true);
    }, []);

    return (
        <Container>
            <AlertsListHeader>
                {`${translate('hourlyChart')}`}
                <InformationIconWrapper onClick={handleInfoIconClick}>
                    <InfoIcon/>
                </InformationIconWrapper>
            </AlertsListHeader>
            { outdoorSportsDataIsFetching && <HourlyChartLoadingUI /> }
            { !outdoorSportsDataIsFetching && !forecastRecords && !outdoorSportsIndexLegendLevels && isApiError && <OutdoorSportsLoadingErrorUI /> }
            { !outdoorSportsDataIsFetching && forecastRecords && outdoorSportsIndexLegendLevels && !isApiError
                && <>
                    {!isMobile && <><LegendBar>
                        {outdoorSportsIndexLegendLevels?.map((indexLegendLevel, i) => {
                            return <div key={i}>{indexLegendLevel?.indexLevelDisplayStr}</div>;
                        })}
                    </LegendBar><LegendBarBorder />
                    </>}
                    <BarChartContainer>
                        <BarChart height={290} margin={{ top: 40, right: 15, bottom: 0, left: 15 }} data={forecastRecords}>
                            <CartesianGrid />
                            <XAxis tick={false} hide fill='#EEEEEE' />
                            <Bar 
                                dataKey='indexNumber' 
                                barSize={barWdith}
                                fill='#C7C7CC'
                                isAnimationActive={false}
                            >
                                {
          	                        forecastRecords.map((forecastRecord, i) => {
            	                        const color = forecastRecord.isDayTime? '#C7C7CC' : '#8E8E93';
            	                        return <Cell key={i} fill={color} />;
                                    })
                                }
                                <LabelList dataKey='indexNumber' position='top' fill='#8E8E93' />
                            </Bar>
                        </BarChart>
                    </BarChartContainer>
                    <HourBar>
                        {forecastRecords?.map(function(forecastRecord, i) {
                            if (forecastRecord?.displayLabelOnChart) {
                                return <div key={i}>{forecastRecord?.dateTimeHourDisplayStr}</div>;
                            }
                        })}
                    </HourBar>
                </>
            }
            { showIndexPopup 
                && <OutdoorSportsIndexExplanatoryPopup 
                    outdoorSportsIndexLegendLevels={outdoorSportsIndexLegendLevels}
                    open={showIndexPopup} 
                    onClose={()=>setShowIndexPopup(false)} 
                /> 
            }
        </Container>
    );
}));

const Container = styled.div`
    display: flex;
    flex-direction: column;
    background-color: #F9F9F9;
    height: 450px;
    width: 100%;
    font-family: Source Sans Pro;
    font-style: normal;
    font-weight: 500;
    font-size: 15px;
    color: #555555;
    margin: 10px 10px 0 0;
    text-align: center;

    .recharts-wrapper .recharts-cartesian-grid-vertical line:first-child,
    .recharts-wrapper .recharts-cartesian-grid-vertical line:last-child {
        stroke-opacity: 0 !important;
    }

    @media ${mediumMediaQuery} {
        margin: 0 0;
    }
    
    @media ${smallMediaQuery} {
        height: 400px;
        margin-right: 0;
    }
`;

const InformationIconWrapper = styled.a`
    cursor: pointer;
    margin: 12px 10px 0 10px;
    margin: 0 0 0 auto;
`;

const LegendBar = styled.div`
    display: flex;
    flex-direction: row;
    padding: 15px 60px;
    justify-content: space-between;
    width: 100%;
    height: 50px;
`;

const LegendBarBorder = styled.hr`
    height: 1px;
    color: #d6d6d6;
    background-color: #d6d6d6;
    border: none;
    width: 96%;
`; 

const BarChartContainer = styled(ResponsiveContainer)`
    display: inline-block;
`;

const HourBar = styled.div`
    display: flex;
    flex-direction: row;
    padding: 10px 30px 2% 30px;
    justify-content: space-between;
    width: 100%;
    height: 50px;

    @media ${largeMediaQuery} {
        padding-bottom: 15px;
    }
`;
